// paywallFlow.ts — the paywall's purchase / restore orchestration with every side effect
// injected, so it can be unit-tested in node. MembershipPaywall wires the real deps.
import type { BuyResult } from '@/services/purchases';

export interface FlowDeps {
  buy: () => Promise<BuyResult>;
  restore: () => Promise<boolean>;
  sync: () => Promise<unknown>;
  recheck: () => Promise<boolean>;
  sleep: (ms: number) => Promise<void>;
  /** False once the paywall has closed; polling stops and nothing further is reported. */
  isLive?: () => boolean;
  tries?: number;
  gapMs?: number;
}

export type FlowOutcome = 'member' | 'pending' | 'cancelled' | 'not-found' | 'aborted';

export const POLL_TRIES = 4;
export const POLL_GAP_MS = 2000;

/** Opening check: a user who is already a member never sees the purchase options. */
export async function alreadyMember(deps: Pick<FlowDeps, 'recheck'>): Promise<boolean> {
  return deps.recheck().catch(() => false);
}

export async function confirmMembership(deps: FlowDeps): Promise<'member' | 'pending' | 'aborted'> {
  const live = deps.isLive ?? (() => true);
  const tries = deps.tries ?? POLL_TRIES;
  const gap = deps.gapMs ?? POLL_GAP_MS;
  try { await deps.sync(); } catch { /* the webhook will catch up */ }
  for (let i = 0; i < tries; i++) {
    if (!live()) return 'aborted';
    if (await deps.recheck().catch(() => false)) return live() ? 'member' : 'aborted';
    if (i < tries - 1) await deps.sleep(gap);
  }
  return live() ? 'pending' : 'aborted';
}

/** buy -> sync -> recheck. A cancel is silent: no sync. Real errors propagate. */
export async function runPurchase(deps: FlowDeps): Promise<FlowOutcome> {
  if ((await deps.buy()) === 'cancelled') return 'cancelled';
  return confirmMembership(deps);
}

/** restore -> (only if the store account has the entitlement) sync -> recheck. */
export async function runRestore(deps: FlowDeps): Promise<FlowOutcome> {
  if (!(await deps.restore())) return 'not-found';
  return confirmMembership(deps);
}
